import moment from 'moment';
import { supabase } from './supabaseClient';

export const WARMUP_TARGETS = {
    CLIPS_TARGET: 5,
    SEARCH_TAB_TARGET: 5,
    MIN_TOTAL_ACTIVITIES: 10,
};

export const INFRASTRUCTURE_ERROR_CODES = [
    'IP_NOT_CHANGED',
    'WEBDRIVER_SESSION_FAILED',
];

export interface IWarmupProgress {
    id: string;
    device_app_account_id: string;
    clips_completed: number;
    search_tab_completed: number;
    created_at: string;
    updated_at: string;
}

export interface IWarmupProgressWithAccount extends IWarmupProgress {
    device_app_account: IDeviceAppAccount;
}

export interface IDevice {
    device_id: string; // UDID를 PK로 사용
    label: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface IDeviceAppAccount {
    id: string;
    player_id: string | null;
    device_id: string; // 이제 직접 UDID를 저장하며 devices 테이블의 PK를 참조
    app_package_name: string; // app_id 대신 직접 패키지명 저장
    label: string | null;
    busy: boolean | null;
    last_launched_at: string | null;
    next_available_at: string | null;
    warmed_up: boolean | null;
    created_at: string;
    updated_at: string | null;
    device?: IDevice; // 조인 시 포함될 수 있음
}

export type DeviceSelectionMode = 'warmed_up_first' | 'not_warmed_up_first' | 'random';

export interface IWorkerControls {
    id: string;
    is_running: boolean | null;
    schedule_enabled: boolean | null;
    device_selection_mode: DeviceSelectionMode | null;
    updated_at?: string | null;
}

export interface IScheduleLog {
    id: string;
    device_app_account_id: string;
    task_type: string;
    status: string;
    started_at: string | null;
    ended_at: string | null;
    metadata: any | null;
    created_at: string | null;
    device_app_accounts?: IDeviceAppAccount;
}

export interface ITaskDecisionConfig {
    id: string;
    clips_gap_minutes: number;
    follow_gap_minutes: number;
    like_gap_minutes: number;
    max_daily_activities: number;
    max_follow_per_target_id_hourly: number | null;
    max_like_per_target_url_hourly: number | null;
    same_device_cooldown_seconds: number;
    schedule_gap_minutes: number;
    search_tab_gap_minutes: number;
    small_order_threshold: number | null;
    small_order_protection_remaining: number | null;
    large_order_protection_remaining: number | null;
    updated_at: string;
}

const getWorkerControls = async (): Promise<IWorkerControls | null> => {
    const { data, error } = await supabase
        .from('worker_controls')
        .select('*')
        .limit(1)
        .maybeSingle();

    if (error) {
        console.error('Failed to fetch worker_controls:', error);
        return null;
    }

    if (!data) {
        return null;
    }

    return data as IWorkerControls;
};

const updateWorkerControls = async (patch: Partial<Pick<IWorkerControls, 'is_running' | 'schedule_enabled' | 'device_selection_mode'>>) => {
    const { data: row, error: selectError } = await supabase
        .from('worker_controls')
        .select('id')
        .limit(1)
        .single();

    if (selectError || !row) {
        throw selectError || new Error('No worker_controls row found');
    }

    const { error: updateError } = await supabase
        .from('worker_controls')
        .update({
            ...patch,
            updated_at: new Date().toISOString(),
        })
        .eq('id', row.id);

    if (updateError) {
        throw updateError;
    }
};

// 일자별로 모든 orderTask의 작업 진행량을 조회하는 함수
const getDailyScheduleLogsByOrderTasks = async (
    startDate?: string,
    endDate?: string
): Promise<{
    dailyStats: Record<string, { date: string; totalLogs: number; successLogs: number; failLogs: number; orderTaskCounts: Record<string, { total: number; success: number; fail: number }>; errorReasons: Record<string, number> }>;
    orderTaskTotals: Record<string, { total: number; success: number; fail: number }>;
}> => {
    // 페이지네이션을 사용하여 모든 데이터 가져오기
    let allLogs: any[] = [];
    let from = 0;
    const pageSize = 10000;
    let hasMore = true;

    while (hasMore) {
        let query = supabase
            .from('account_logs')
            .select(`
                created_at,
                status,
                metadata
            `)
            .in('task_type', ['schedule', 'like', 'follow'])
            .order('created_at', { ascending: false })
            .range(from, from + pageSize - 1);

        if (startDate) {
            query = query.gte('created_at', startDate);
        }
        if (endDate) {
            query = query.lte('created_at', endDate);
        }

        const { data, error } = await query;

        if (error) {
            console.error('Error fetching daily schedule logs:', error);
            break;
        }

        if (!data || data.length === 0) {
            hasMore = false;
        } else {
            allLogs = [...allLogs, ...data];
            hasMore = data.length === pageSize;
            from += pageSize;
        }
    }

    const logs = allLogs;
    console.log(`총 ${logs.length}개의 스케줄 로그를 가져왔습니다.`);

    const dailyStats: Record<string, {
        date: string;
        totalLogs: number;
        successLogs: number;
        failLogs: number;
        orderTaskCounts: Record<string, { total: number; success: number; fail: number }>;
        errorReasons: Record<string, number>; // reason별 에러 수량
    }> = {};
    const orderTaskTotals: Record<string, { total: number; success: number; fail: number }> = {};

    // infrastructure error인지 확인하는 함수
    const isInfrastructureError = (log: any): boolean => {
        // 성공한 작업은 항상 실제 작업으로 간주
        if (log.status === 'success') {
            return false;
        }

        // 실패한 작업만 인프라 에러 체크
        if (log.status === 'fail') {
            const metadata = log.metadata as any;

            // metadata.reason 체크 (clips, search_tab)
            if (metadata?.reason && INFRASTRUCTURE_ERROR_CODES.includes(metadata.reason)) {
                return true;
            }

            // metadata.results 체크 (schedule)
            if (metadata?.results && Array.isArray(metadata.results)) {
                const hasOnlyInfrastructureErrors = metadata.results.every((result: any) =>
                    result.reason && INFRASTRUCTURE_ERROR_CODES.includes(result.reason)
                );
                if (hasOnlyInfrastructureErrors) {
                    return true;
                }
            }
        }

        return false; // 실제 작업이 수행된 경우
    };

    logs.forEach(log => {
        const date = moment(log.created_at).utcOffset('+09:00').format('YYYY-MM-DD');
        // metadata.results[0].orderTaskId에서 orderTaskId 추출
        const orderTaskId = (log.metadata as any)?.results?.[0]?.orderTaskId;
        const isSuccess = log.status === 'success';
        const isInfraError = isInfrastructureError(log);

        // 일자별 통계 초기화
        if (!dailyStats[date]) {
            dailyStats[date] = {
                date,
                totalLogs: 0,
                successLogs: 0,
                failLogs: 0,
                orderTaskCounts: {},
                errorReasons: {}
            };
        }

        // orderTask별 총계 초기화
        if (orderTaskId && !orderTaskTotals[orderTaskId]) {
            orderTaskTotals[orderTaskId] = { total: 0, success: 0, fail: 0 };
        }

        // 일자별 전체 통계 업데이트
        dailyStats[date].totalLogs += 1;
        if (isSuccess) {
            dailyStats[date].successLogs += 1;
        } else if (!isInfraError) {
            // infrastructure error가 아닌 경우에만 실패로 카운트
            dailyStats[date].failLogs += 1;

            // 실패한 로그의 reason 추출 및 카운트
            const metadata = log.metadata as any;
            let reason = 'UNKNOWN';

            // metadata.reason 체크 (clips, search_tab)
            if (metadata?.reason) {
                reason = metadata.reason;
            }
            // metadata.results 체크 (schedule - like, follow)
            else if (metadata?.results && Array.isArray(metadata.results)) {
                // results의 모든 reason을 수집
                const reasons = metadata.results
                    .map((result: any) => result.reason)
                    .filter((r: any) => r);
                if (reasons.length > 0) {
                    // 가장 많이 등장한 reason 사용
                    reason = reasons[0];
                }
            }

            // errorReasons 카운트 증가
            if (!dailyStats[date].errorReasons[reason]) {
                dailyStats[date].errorReasons[reason] = 0;
            }
            dailyStats[date].errorReasons[reason] += 1;
        }

        // 일자별 orderTask별 통계 업데이트
        if (orderTaskId) {
            if (!dailyStats[date].orderTaskCounts[orderTaskId]) {
                dailyStats[date].orderTaskCounts[orderTaskId] = { total: 0, success: 0, fail: 0 };
            }
            dailyStats[date].orderTaskCounts[orderTaskId].total += 1;
            if (isSuccess) {
                dailyStats[date].orderTaskCounts[orderTaskId].success += 1;
            } else if (!isInfraError) {
                // infrastructure error가 아닌 경우에만 실패로 카운트
                dailyStats[date].orderTaskCounts[orderTaskId].fail += 1;
            }

            // orderTask별 총계 업데이트
            orderTaskTotals[orderTaskId].total += 1;
            if (isSuccess) {
                orderTaskTotals[orderTaskId].success += 1;
            } else if (!isInfraError) {
                // infrastructure error가 아닌 경우에만 실패로 카운트
                orderTaskTotals[orderTaskId].fail += 1;
            }
        }
    });

    return { dailyStats, orderTaskTotals };
};

// 디바이스별 시간당 성공 수량을 조회하는 함수
const getDeviceHourlySuccessCount = async (): Promise<Record<string, number>> => {
    try {
        // 최근 1시간의 데이터 범위 설정
        const endDate = moment().utc().format('YYYY-MM-DD HH:mm:ss');
        const startDate = moment().subtract(1, 'hours').utc().format('YYYY-MM-DD HH:mm:ss');

        // account_logs에서 device_app_accounts와 devices를 조인하여 디바이스별 성공 로그 조회
        const { data, error } = await supabase
            .from('account_logs')
            .select(`
                status,
                device_app_accounts!inner(
                    device_id,
                    devices!inner(
                        device_id,
                        label
                    )
                )
            `)
            .in('task_type', ['schedule', 'like', 'follow'])
            .eq('status', 'success')
            .gte('created_at', startDate)
            .lte('created_at', endDate);

        if (error) {
            console.error('Error fetching device hourly success count:', error);
            return {};
        }

        // 디바이스별 성공 수량 집계
        const deviceSuccessCount: Record<string, number> = {};

        data?.forEach((log: any) => {
            const deviceId = log.device_app_accounts?.device_id;
            if (deviceId) {
                deviceSuccessCount[deviceId] = (deviceSuccessCount[deviceId] || 0) + 1;
            }
        });

        return deviceSuccessCount;
    } catch (error) {
        console.error('Error in getDeviceHourlySuccessCount:', error);
        return {};
    }
};

// orderTask별 노르망디 작업 성공 개수를 조회하는 함수 (최근 1시간)
const getNormandySuccessCountsLastHour = async (): Promise<Record<string, number>> => {
    try {
        // 최근 1시간의 데이터 범위 설정
        const endDate = moment().utc().format('YYYY-MM-DD HH:mm:ss');
        const startDate = moment().subtract(1, 'hours').utc().format('YYYY-MM-DD HH:mm:ss');

        const { data, error } = await supabase
            .from('account_logs')
            .select('metadata')
            .in('task_type', ['schedule', 'like', 'follow'])
            .eq('status', 'success')
            .gte('created_at', startDate)
            .lte('created_at', endDate);

        if (error) {
            console.error('Error fetching normandy success counts:', error);
            return {};
        }

        // orderTaskId별 성공 수량 집계
        const orderTaskSuccessCount: Record<string, number> = {};

        data?.forEach((log: any) => {
            const orderTaskId = log.metadata?.results?.[0]?.orderTaskId;
            if (orderTaskId) {
                orderTaskSuccessCount[orderTaskId] = (orderTaskSuccessCount[orderTaskId] || 0) + 1;
            }
        });

        return orderTaskSuccessCount;
    } catch (error) {
        console.error('Error in getNormandySuccessCountsLastHour:', error);
        return {};
    }
};

/**
 * order_task_id들에 대한 활성 lock 상태를 조회합니다
 * @param orderTaskIds order_task_id 배열
 * @returns orderTaskId를 키로, lock 존재 여부를 값으로 하는 Record
 */
const getActiveOrderLocks = async (orderTaskIds: string[]): Promise<Record<string, boolean>> => {
    if (!orderTaskIds || orderTaskIds.length === 0) {
        return {};
    }

    const now = new Date().toISOString();

    const { data, error } = await supabase
        .from('order_worker_locks')
        .select('order_task_id')
        .in('order_task_id', orderTaskIds)
        .gt('expires_at', now);

    if (error) {
        console.error('Error fetching order worker locks:', error);
        return {};
    }

    // order_task_id를 키로 하는 Record 생성
    const lockStatus: Record<string, boolean> = {};
    orderTaskIds.forEach(id => {
        lockStatus[id] = false;
    });

    data?.forEach(lock => {
        lockStatus[lock.order_task_id] = true;
    });

    return lockStatus;
};

/**
 * Task decision config를 조회합니다
 * @returns task_decision_config 데이터 또는 null
 */
const getTaskDecisionConfig = async (): Promise<ITaskDecisionConfig | null> => {
    const { data, error } = await supabase
        .from('task_decision_config')
        .select('*')
        .limit(1)
        .maybeSingle();

    if (error) {
        console.error('Failed to fetch task_decision_config:', error);
        return null;
    }

    return data as ITaskDecisionConfig | null;
};

/**
 * 예행연습 진도 계산 유틸리티
 */
const calculateWarmupMetrics = (progress: { clips_completed: number; search_tab_completed: number }) => {
    const totalActivities = progress.clips_completed + progress.search_tab_completed;

    return {
        totalActivities,
        clipsProgress: Math.min(100, (progress.clips_completed / WARMUP_TARGETS.CLIPS_TARGET) * 100),
        searchTabProgress: Math.min(100, (progress.search_tab_completed / WARMUP_TARGETS.SEARCH_TAB_TARGET) * 100),
        overallProgress: Math.min(100, (totalActivities / WARMUP_TARGETS.MIN_TOTAL_ACTIVITIES) * 100),
        isClipsReady: progress.clips_completed >= WARMUP_TARGETS.CLIPS_TARGET,
        isSearchTabReady: progress.search_tab_completed >= WARMUP_TARGETS.SEARCH_TAB_TARGET,
        isReadyForSetup: (
            progress.clips_completed >= WARMUP_TARGETS.CLIPS_TARGET &&
            progress.search_tab_completed >= WARMUP_TARGETS.SEARCH_TAB_TARGET &&
            totalActivities >= WARMUP_TARGETS.MIN_TOTAL_ACTIVITIES
        )
    };
};

/**
 * 예행연습 진도 리포트 조회 (관리자용) - 계산된 메트릭 포함
 */
const getWarmupProgressReport = async (): Promise<Array<IWarmupProgressWithAccount & { metrics: ReturnType<typeof calculateWarmupMetrics> }>> => {
    const { data, error } = await supabase
        .from('account_warmup_progress')
        .select(`
            *,
            device_app_account:device_app_accounts (
                *,
                device:devices (
                    device_id,
                    label,
                    created_at,
                    updated_at
                )
            )
        `)
        .eq('device_app_account.warmed_up', false)
        .order('clips_completed', { ascending: false })
        .order('search_tab_completed', { ascending: false });

    if (error) {
        console.error('예행연습 진도 리포트 조회 실패:', error);
        return [];
    }

    // 각 계정별 메트릭 계산 포함
    return (data || []).map(item => ({
        ...item,
        metrics: calculateWarmupMetrics(item)
    })) as Array<IWarmupProgressWithAccount & { metrics: ReturnType<typeof calculateWarmupMetrics> }>;
};

const supabaseRepository = {
    getWorkerControls,
    updateWorkerControls,
    getDailyScheduleLogsByOrderTasks,
    getDeviceHourlySuccessCount,
    getNormandySuccessCountsLastHour,
    getActiveOrderLocks,
    getTaskDecisionConfig,
    calculateWarmupMetrics,
    getWarmupProgressReport,
};

export default supabaseRepository;
